import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Alert,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ArrowBack, Refresh } from '@mui/icons-material';
import { ConsumerProofOfDelivery } from '@/api/bol-notifications';
import { 
  PageLayout, 
  DataGridContainer, 
  JsonDetailPanel,
} from '@/components';
import NotificationPayloadDialog from '@/components/bol-notifications/consumer/NotificationPayloadDialog';
import { 
  createConsumerProofOfDeliveryColumns,
  defaultConsumerProofOfDeliveryColumnVisibilityModel,
} from '@/components/bol-notifications/consumer/ConsumerProofsOfDeliveryColumns';
import { 
  useDataGridSelection, 
  usePagination,
  useConsumerProofOfDelivery,
} from '@/hooks';

const ConsumerProofsOfDeliveryPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get parameters from URL
  const subscriptionId = searchParams.get('subscriptionId') || '';
  const consumerId = searchParams.get('consumerId') || '';
  const consumerName = searchParams.get('consumerName') || 'Consumer';
  
  // Column visibility state
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    defaultConsumerProofOfDeliveryColumnVisibilityModel
  );
  
  // Modal state for notification payload
  const [payloadDialogOpen, setPayloadDialogOpen] = useState(false);
  const [selectedPayload, setSelectedPayload] = useState<string | null>(null);
  
  // Use custom hooks
  const {
    proofsOfDelivery,
    loading,
    total,
    fetchProofsOfDelivery,
  } = useConsumerProofOfDelivery({
    subscriptionId,
    consumerId,
  });

  const { 
    selectedItem: selectedProofForDetail, 
    selectedRowId,
    handleRowSelectionChange,
    handleCloseDetailPanel 
  } = useDataGridSelection<ConsumerProofOfDelivery>({ 
    data: proofsOfDelivery, 
    getItemId: (item) => item.id || '' 
  });

  const {
    pagination,
    handlePaginationModelChange,
  } = usePagination();

  // Event handlers
  const handleRefresh = () => {
    fetchProofsOfDelivery(pagination.page, pagination.pageSize);
  };

  const handleBackToConsumers = () => {
    navigate(`/consumers?subscriptionId=${subscriptionId}`);
  };

  const handlePayloadClick = (payload: string) => {
    setSelectedPayload(payload);
    setPayloadDialogOpen(true);
  };

  const handleClosePayloadDialog = () => {
    setPayloadDialogOpen(false);
    setSelectedPayload(null);
  };

  // Create columns
  const columns = createConsumerProofOfDeliveryColumns(handlePayloadClick);

  // Fetch proofs of delivery on mount and when pagination changes
  useEffect(() => {
    if (subscriptionId && consumerId) {
      fetchProofsOfDelivery(pagination.page, pagination.pageSize);
    }
  }, [subscriptionId, consumerId, pagination.page, pagination.pageSize, fetchProofsOfDelivery]);

  if (!subscriptionId || !consumerId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Alert severity="error">
          Missing subscription ID or consumer ID. Please navigate from the consumers page.
        </Alert>
      </Box>
    );
  }

  return (
    <PageLayout 
      title={`Proofs of Delivery - ${consumerName}`}
      selectedItemInfo={selectedProofForDetail ? `Selected: ${selectedProofForDetail.id}` : undefined}
      leftTitleActions={
        <Tooltip title="Refresh proofs of delivery list">
          <IconButton
            onClick={handleRefresh}
            size="small"
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      }
      titleActions={
        <Button 
          onClick={handleBackToConsumers}
          startIcon={<ArrowBack />}
          variant="contained"
          color="primary"
          size="medium"
        >
          Back to Consumers
        </Button>
      }
      sidePanel={selectedProofForDetail ? (
        <JsonDetailPanel
          title="Proofs of Delivery Details"
          data={selectedProofForDetail}
          onClose={handleCloseDetailPanel}
        />
      ) : undefined}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        <DataGridContainer
          rows={proofsOfDelivery}
          columns={columns}
          loading={loading}
          selectedRowId={selectedRowId}
          onRowSelectionChange={handleRowSelectionChange}
          paginationModel={pagination}
          rowCount={total}
          onPaginationModelChange={handlePaginationModelChange}
          getRowId={(row: ConsumerProofOfDelivery) => row.id}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(model: React.SetStateAction<Record<string, boolean>>) => 
            setColumnVisibilityModel(model)
          }
          columnOrderStorageKey="consumer-proof-of-delivery-columns"
        />
      </Box>

      <NotificationPayloadDialog
        open={payloadDialogOpen}
        onClose={handleClosePayloadDialog}
        payload={selectedPayload}
      />
    </PageLayout>
  );
};

export default ConsumerProofsOfDeliveryPage;
